import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { OrgAvatar } from '@/shared/components/custom/avatars/OrgAvatar';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { PALETTE } from '@/core/lib/color-palette';
import type { InvitationView } from '../types';

interface InvitationDialogContentProps {
  invitation: InvitationView;
  onAccept?: () => void;
  onReject: () => void;
  isPending: boolean;
}

function getInviterColorToken(name: string): string {
  const index = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[index % PALETTE.length].token;
}

/**
 * Shared content for invitation accept/reject flows. Both the in-app dialog and
 * the email-link page feed it an `InvitationView`. `onAccept` is optional so a
 * caller can render reject-only when needed.
 */
export function InvitationDialogContent({
  invitation,
  onAccept,
  onReject,
  isPending,
}: InvitationDialogContentProps) {
  const { t } = useTranslation('invitations');

  const inviterName = invitation.inviterName ?? invitation.tenantName;
  const inviterColor = getInviterColorToken(inviterName);

  return (
    <div className="flex flex-col gap-5">
      {/* Inviter */}
      <div className="flex items-center gap-3">
        <UserAvatar name={inviterName} color={inviterColor} size="md" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-primary truncate">{inviterName}</p>
          <p className="text-xs text-muted-foreground">{t('dialog.inviterAction')}</p>
        </div>
      </div>

      {/* Org — single row */}
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted/40">
        <OrgAvatar
          name={invitation.tenantName}
          color="indigo-nebula"
          src={invitation.tenantAvatarUrl ?? undefined}
          size="sm"
        />
        <p className="font-bold text-sm flex-1 truncate">{invitation.tenantName}</p>
        {invitation.membersCount != null && (
          <Badge variant="secondary">{t('membersCount', { count: invitation.membersCount })}</Badge>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 md:flex-row md:justify-end">
        <Button
          data-testid="invitations-dialog-content-reject-btn"
          variant="outline"
          onClick={onReject}
          disabled={isPending}
          className="w-full md:w-auto"
        >
          {t('dialog.reject')}
        </Button>
        {onAccept && (
          <Button
            data-testid="invitations-dialog-content-accept-btn"
            onClick={onAccept}
            disabled={isPending}
            className="w-full md:w-auto"
          >
            {t('dialog.accept')}
          </Button>
        )}
      </div>
    </div>
  );
}
